import * as React from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { AdminScreen } from '@app/Screens/types';
import { STATUS_META } from '@app/Screens/StatusBadge';
import { colors } from '@app/components/designTokens';

interface ScreensMapProps {
  screens: AdminScreen[];
  onSelect: (screenId: number) => void;
  height?: number | string;
}

const DEFAULT_CENTER = { lat: 39.5, lng: -98.35 }; // US centroid fallback

/** Shared notice box for the no-API-key / loading / error states. */
const MapNotice: React.FunctionComponent<{ title: string; body?: string; height: number | string }> = ({
  title,
  body,
  height
}) => (
  <div
    style={{
      height,
      borderRadius: 16,
      border: `1px dashed ${colors.border}`,
      background: '#fff',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      textAlign: 'center',
      padding: 24
    }}
  >
    <div style={{ fontWeight: 600, color: colors.title }}>{title}</div>
    {body && <div style={{ fontSize: 13, color: colors.subtle, maxWidth: 420 }}>{body}</div>}
  </div>
);

export const ScreensMap: React.FunctionComponent<ScreensMapProps> = ({
  screens,
  onSelect,
  height = 560
}) => {
  const apiKey = process.env.GOOGLE_MAPS_API_KEY || '';
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'advolt-admin-google-maps',
    googleMapsApiKey: apiKey
  });

  const located = screens.filter(s => s.latitude != null && s.longitude != null);

  const center = React.useMemo(() => {
    if (located.length === 0) return DEFAULT_CENTER;
    const lat = located.reduce((a, s) => a + (s.latitude as number), 0) / located.length;
    const lng = located.reduce((a, s) => a + (s.longitude as number), 0) / located.length;
    return { lat, lng };
  }, [located]);

  if (!apiKey) {
    return (
      <MapNotice
        title="Map unavailable — no Google Maps API key"
        body="Set GOOGLE_MAPS_API_KEY (restricted to the admin origin) in the build environment to enable the screens map. The list view works without it."
        height={height}
      />
    );
  }
  if (loadError) {
    return <MapNotice title="Failed to load Google Maps" body={String(loadError)} height={height} />;
  }
  if (!isLoaded) {
    return <MapNotice title="Loading map…" height={height} />;
  }

  return (
    <div style={{ borderRadius: 16, overflow: 'hidden', border: `1px solid ${colors.border}` }}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: typeof height === 'number' ? `${height}px` : height }}
        center={center}
        zoom={located.length ? 11 : 4}
        options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
      >
        {located.map(s => (
          <Marker
            key={s.screenId}
            position={{ lat: s.latitude as number, lng: s.longitude as number }}
            title={`${s.displayName || `Screen #${s.screenId}`} · ${s.status}`}
            onClick={() => onSelect(s.screenId)}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: STATUS_META[s.status].dot,
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2
            }}
          />
        ))}
      </GoogleMap>
    </div>
  );
};
